import { SMap } from '@avantia/client-and-server-utilities';
import { HpError } from '@avantia/logging-and-errors';
import { HpAnswerMap } from '@avantia/questionset-model';
import { HpSvgIconTypes, SvgIcon } from '@avantia/style-guide';
import { BasicHeadingControl } from '../../engineClient/components/headings/basicHeadingControl';
import { HelpWidget } from '../../engineClient/components/widgets/helpInfoWidget';
import { coverTypeMap, getCopyTextFromCustomData, HpClaimsCommonCustomData } from '../../tools/utilities';
import { AccidentalDamageLevelCoverMap, CoverType, Policy } from '../../types/policyTypes';
import { questionIds } from '../../types/questionConstants';

const keySeparator = '~';
const perilCoverKey = (peril: string, coverType: string) => `${peril}${keySeparator}${coverType}`;
const perilCoverAdKey = (peril: string, coverType: string, adLevel: string) =>
  `${peril}${keySeparator}${coverType}${keySeparator}${adLevel}`;

interface CoveredNotCoveredCardProps {
  footerDescription?: string;
  headerDescription?: string;
  items?: string[];
}

export interface PerilCopyText {
  heading: string;
  information?: string | null;
  sameAs: keyof CoverType;
  subHeading: string;
  whatsCovered: CoveredNotCoveredCardProps;
  whatsNotCovered: CoveredNotCoveredCardProps | null;
  showExitButton?: boolean;
}

interface GetPerilInformationWidgets {
  answers: HpAnswerMap;
  policy: Policy;
  perilCopyText: { [key: string]: PerilCopyText };
  commonCustomData: HpClaimsCommonCustomData;
}

export function getPerilInclusionsWidgets({
  answers,
  perilCopyText,
  policy,
  commonCustomData
}: GetPerilInformationWidgets): CreateInclusionsWidgetResult {
  const { perilType, coverType } = questionIds;
  const peril = answers[perilType].customer as string;
  const coverTypeAnswer = answers[coverType].customer as keyof CoverType;
  if (peril && policy && coverType) {
    return createCoverageInformationWidget({
      coverType: coverTypeAnswer,
      accidentalDamageLevelMap: policy.accidentalDamageLevel,
      textMap: perilCopyText,
      peril,
      commonCustomData
    });
  } else {
    throw new HpError(
      'Unable to create peril information widgets. This can only be created if the peril, the policy and the cover type have been selected.'
    );
  }
}

interface CreateCoverageInformationWidgetProps {
  accidentalDamageLevelMap: AccidentalDamageLevelCoverMap;
  adLevel?: string;
  coverType: keyof CoverType;
  isSummary?: boolean;
  peril: string;
  textMap: SMap<PerilCopyText>;
  commonCustomData: HpClaimsCommonCustomData;
}

interface CreatePerilInformationWidgetResult {
  coverTypeTitle: string;
  whatsCovered: JSX.Element;
  whatsNotCovered: JSX.Element | null;
}

interface CreateInclusionsWidgetResult {
  perilHeader: JSX.Element;
  perilInformationWidget: CreatePerilInformationWidgetResult[];
}

function createCoverageInformationWidget({
  accidentalDamageLevelMap,
  coverType,
  peril,
  textMap,
  commonCustomData
}: CreateCoverageInformationWidgetProps): CreateInclusionsWidgetResult {
  let perilCopyText = textMap[peril];
  if (!perilCopyText) {
    let key = perilCoverKey(peril, coverType);
    perilCopyText = textMap[key];
    if (perilCopyText) {
      const { sameAs } = perilCopyText;

      if (sameAs === coverTypeMap.BuildingsAndContents) {
        // We know it's B&C AD.
        const { Buildings, Contents } = coverTypeMap;
        const buildingsKey = perilCoverAdKey(peril, Buildings, accidentalDamageLevelMap[Buildings as string]);
        const contentsKey = perilCoverAdKey(peril, Contents, accidentalDamageLevelMap[Contents as string]);
        const buildingsCard = createInclusionsWidget({
          coverType: Buildings,
          perilText: updatePerilCopyTextByCopyTextKey(textMap[buildingsKey], commonCustomData)
        });
        const contentsCard = createInclusionsWidget({
          coverType: Contents,
          perilText: updatePerilCopyTextByCopyTextKey(textMap[contentsKey], commonCustomData)
        });
        return {
          perilHeader: createPerilHeader(updatePerilCopyTextByCopyTextKey(perilCopyText, commonCustomData)),
          perilInformationWidget: [buildingsCard, contentsCard]
        };
      } else if (sameAs) {
        // It's not B&C AD
        key = perilCoverKey(peril, sameAs);
        perilCopyText = textMap[key];
        if (!perilCopyText) {
          throw new HpError(`Invalid custom data for key ${key}`);
        }
      }
    } else {
      const adLevel = accidentalDamageLevelMap[coverType];
      key = perilCoverAdKey(peril, coverType, adLevel as string);
      perilCopyText = textMap[key];
    }
  }

  const updatedPerilCopyText = updatePerilCopyTextByCopyTextKey(perilCopyText, commonCustomData);
  return {
    perilHeader: createPerilHeader(updatePerilCopyTextByCopyTextKey(perilCopyText, commonCustomData)),
    perilInformationWidget: [createInclusionsWidget({ coverType: null, perilText: updatedPerilCopyText })]
  };
}

interface CreatePerilInformationWidgetProps {
  coverType: keyof CoverType | null;
  perilText: PerilCopyText;
}

function createInclusionsWidget({
  coverType,
  perilText
}: CreatePerilInformationWidgetProps): CreatePerilInformationWidgetResult {
  const { whatsCovered, whatsNotCovered } = perilText;
  return {
    coverTypeTitle: `Your ${coverType || ''} cover`,
    whatsCovered: createCoveredNotCoveredCard(whatsCovered, 'covered'),
    whatsNotCovered: whatsNotCovered && createCoveredNotCoveredCard(whatsNotCovered, 'not-covered')
  };
}

function createItemsSet(items: string[], iconType: HpSvgIconTypes) {
  return (
    <ul>
      {items.map((item, index) => (
        <div key={index}>
          <li className="list-unstyled">
            <SvgIcon type={iconType} size="very-tiny" />
            <span>{item}</span>
          </li>
        </div>
      ))}
    </ul>
  );
}

function createPerilHeader(perilCopy: PerilCopyText): JSX.Element {
  const { heading, subHeading, information } = perilCopy;
  return (
    <>
      <BasicHeadingControl heading={heading} subheading={subHeading} />
      {information && (
        <div className="hp-peril-help-widget">
          <HelpWidget helpText={information} />
        </div>
      )}
    </>
  );
}

function createCoveredNotCoveredCard(
  coveredNotCovered: CoveredNotCoveredCardProps,
  type: 'covered' | 'not-covered'
): JSX.Element {
  const { items, headerDescription, footerDescription } = coveredNotCovered;
  const icon = (type === 'covered' ? 'check-circle' : 'cross-circle-white') as HpSvgIconTypes;
  return (
    <>
      <div className="cover-information">{headerDescription}</div>
      {items && items.length > 0 ? <div>{createItemsSet(items, icon)}</div> : <></>}
      <div>{footerDescription}</div>
    </>
  );
}

function updatePerilCopyTextByCopyTextKey(
  perilCopyText: PerilCopyText,
  commonCustomData: HpClaimsCommonCustomData
): PerilCopyText {
  const { accidentalDamage: adCommonCopyText } = getCopyTextFromCustomData(commonCustomData);
  if (!adCommonCopyText) {
    throw new HpError('Question set does not contain "accidentalDamage" common copy text map.');
  }

  const { heading, subHeading, information, whatsCovered, whatsNotCovered } = perilCopyText;
  const { headerDescription: whatsCoveredHeader } = whatsCovered || {};
  const { headerDescription: whatsNotCoveredHeader } = whatsNotCovered || {};
  const copyText: Partial<PerilCopyText> = {
    heading: adCommonCopyText[heading] || heading,
    subHeading: adCommonCopyText[subHeading] || subHeading
  };

  if (information) {
    copyText.information = adCommonCopyText[information] || information;
  }

  if (whatsCovered && whatsCoveredHeader) {
    copyText.whatsCovered = {
      ...whatsCovered,
      headerDescription: adCommonCopyText[whatsCoveredHeader] || whatsCoveredHeader
    };
  }

  if (whatsNotCovered && whatsNotCoveredHeader) {
    copyText.whatsNotCovered = {
      ...whatsNotCovered,
      headerDescription: adCommonCopyText[whatsNotCoveredHeader] || whatsNotCoveredHeader
    };
  }

  const updatedCopyText = { ...perilCopyText, ...copyText };
  return updatedCopyText;
}
